import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, List, LogOut, Users, Trophy, Shield, Trash2, HelpCircle, Gift, X, CheckCircle, Clock, Calendar, Flame, TrendingUp, Award, BarChart3, Bug, Download, Share, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import CreateListModal from '@/components/CreateListModal';
import NotificationBell from '@/components/NotificationBell';
import LanguageSelector from '@/components/LanguageSelector';
import WelcomeCarousel from '@/components/WelcomeCarousel';
import InteractiveDemo from '@/components/InteractiveDemo';
import GuidedTour from '@/components/GuidedTour';
import ThemeToggle from '@/components/ThemeToggle';
import useTutorial from '@/hooks/useTutorial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [user, setUser] = useState(location.state?.user || null);
  const [taskLists, setTaskLists] = useState([]);
  const [rewardLists, setRewardLists] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalType, setModalType] = useState('task');
  const [loading, setLoading] = useState(true);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [nextReward, setNextReward] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  // Tutorial state
  const { 
    showCarousel,
    showDemo,
    showTour, 
    completeCarousel,
    completeDemo,
    completeTour,
    resetTutorial 
  } = useTutorial();

  // Tour steps using i18n translations
  const tourSteps = [
    {
      target: '[data-testid="points-card"]',
      title: t('tutorial.steps.points.title'),
      content: t('tutorial.steps.points.content'),
      placement: 'bottom'
    },
    {
      target: '[data-testid="create-task-list-btn"]',
      title: t('tutorial.steps.createTaskList.title'),
      content: t('tutorial.steps.createTaskList.content'),
      placement: 'bottom'
    },
    {
      target: '[data-testid="task-list-rewards-info"]',
      title: t('tutorial.steps.viewRewards.title'),
      content: t('tutorial.steps.viewRewards.content'),
      placement: 'bottom'
    },
    {
      target: '[data-testid="notification-bell"]',
      title: t('tutorial.steps.notifications.title'),
      content: t('tutorial.steps.notifications.content'),
      placement: 'bottom'
    }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Capture install prompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          toast.success(t('install.success'));
          setShowInstallModal(false);
        }
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Install error:', error);
      }
    } else {
      setShowInstallModal(true);
    }
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  const fetchData = async () => {
    try {
      const [userRes, taskListsRes, rewardListsRes, statsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/task-lists`, { withCredentials: true }),
        axios.get(`${API}/reward-lists`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true })
      ]);
      
      // Verify user data is valid
      if (!userRes.data || !userRes.data.user_id) {
        console.log('No valid user session, redirecting to login');
        navigate('/login');
        return;
      }
      
      setUser(userRes.data);
      setTaskLists(taskListsRes.data);
      setRewardLists(rewardListsRes.data);
      setStats(statsRes.data);
      
      // Calculate next reward (closest to achieve)
      await calculateNextReward(rewardListsRes.data, statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // If authentication error (401/403) or network error, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403 || !error.response) {
        console.log('Authentication error, redirecting to login');
        navigate('/login');
        return;
      }
      
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const calculateNextReward = async (rLists, statsData) => {
    try {
      if (!rLists || rLists.length === 0 || !statsData?.points_by_list) {
        setNextReward(null);
        return;
      }

      let closestReward = null;
      let minPointsNeeded = Infinity;
      let canAffordReward = null;

      for (const rewardList of rLists) {
        // Get rewards for this list
        const rewardsRes = await axios.get(`${API}/rewards/${rewardList.list_id}`, { withCredentials: true });
        const availableRewards = rewardsRes.data.filter(r => r.status === 'available');
        
        if (availableRewards.length === 0) continue;

        // Find user's points for the associated task list
        const userPoints = statsData.points_by_list?.find(p => p.list_id === rewardList.task_list_id)?.points || 0;

        // Find cheapest reward
        for (const reward of availableRewards) {
          const pointsNeeded = reward.cost_points - userPoints;
          
          if (pointsNeeded <= 0) {
            // User can afford this reward - show as ready to redeem
            if (!canAffordReward || reward.cost_points < canAffordReward.totalCost) {
              canAffordReward = {
                title: reward.title,
                pointsNeeded: 0,
                totalCost: reward.cost_points,
                currentPoints: userPoints,
                listName: rewardList.name,
                canAfford: true
              };
            }
          } else if (pointsNeeded < minPointsNeeded) {
            // User can't afford yet - track closest
            minPointsNeeded = pointsNeeded;
            closestReward = {
              title: reward.title,
              pointsNeeded: pointsNeeded,
              totalCost: reward.cost_points,
              currentPoints: userPoints,
              listName: rewardList.name,
              canAfford: false
            };
          }
        }
      }

      // Prioritize showing rewards user can afford, otherwise show closest
      setNextReward(canAffordReward || closestReward);
    } catch (error) {
      console.error('Error calculating next reward:', error);
      setNextReward(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const [selectedTaskListId, setSelectedTaskListId] = useState(null);
  
  const handleCreateList = (type, taskListId = null) => {
    setModalType(type);
    setSelectedTaskListId(taskListId);
    setShowCreateModal(true);
  };

  const handleListCreated = () => {
    fetchData();
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#18181B] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="dashboard">
      {/* Header */}
      <header className="mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-[#18181B] dark:border-zinc-600"
                  data-testid="user-avatar"
                />
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-white" data-testid="user-name">
                  {t('dashboard.greeting', { name: user?.name?.split(' ')[0] })}
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-xs sm:text-sm">{t('dashboard.motivation')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {/* Next reward tooltip */}
              {nextReward && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-[#FFC247] border-2 border-[#18181B] rounded-full text-xs font-medium">
                  {nextReward.canAfford ? (
                    <>
                      <span>🎁</span>
                      <span className="max-w-[150px] truncate">{t('dashboard.rewardReady', { reward: nextReward.title })}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold">{nextReward.pointsNeeded} {t('dashboard.pointsWord')}</span>
                      <span className="text-[#18181B]/70">→</span>
                      <span className="max-w-[100px] truncate">{nextReward.title}</span>
                    </>
                  )}
                </div>
              )}
              <ThemeToggle />
              <LanguageSelector />
              <NotificationBell />
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="rounded-full border-2 border-[var(--border-color)] bg-[var(--bg-secondary)] p-2 font-bold hover:opacity-80 transition-all flex items-center justify-center"
                title={t('auth.logout')}
              >
                <LogOut className="w-4 h-4 text-[var(--text-primary)]" />
              </button>
            </div>
          </div>

          {/* Points Card - Clickable */}
          <button
            onClick={() => setShowStatsModal(true)}
            className="w-full text-left bg-[#FFC247] border-2 border-[#18181B] rounded-2xl hard-shadow p-3 sm:p-4 hover:shadow-[6px_6px_0px_0px_rgba(24,24,27,1)] hover:translate-y-[-2px] transition-all cursor-pointer"
            data-testid="points-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs font-bold text-[#18181B]/70 tracking-wide">{t('dashboard.totalPoints')}</p>
                <p className="text-3xl sm:text-4xl font-mono font-bold mt-1" data-testid="total-points">{stats?.total_points || 0}</p>
              </div>
              <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-[#18181B]/20" />
            </div>
            <p className="text-[10px] text-[#18181B]/50 mt-2 text-center">{t('dashboard.tapForStats')}</p>
          </button>
          
          {/* Next reward banner - Mobile only */}
          {nextReward && (
            <div className="sm:hidden mt-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]">
              {nextReward.canAfford ? (
                <>
                  <span className="text-lg">🎁</span>
                  <span className="text-sm font-bold text-zinc-900 dark:text-white">{t('dashboard.rewardReady', { reward: nextReward.title })}</span>
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 text-[#8B5CF6]" />
                  <span className="text-sm">
                    <span className="font-bold text-[#8B5CF6]">{nextReward.pointsNeeded} {t('dashboard.pointsWord')}</span>
                    <span className="text-zinc-500 dark:text-zinc-400"> {t('dashboard.forReward')} </span>
                    <span className="font-medium text-zinc-900 dark:text-white">&ldquo;{nextReward.title}&rdquo;</span>
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {/* Task Lists Section */}
        <section className="mb-8 sm:mb-12">
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <List className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-900 dark:text-white" />
              <h2 className="text-lg sm:text-2xl font-bold text-zinc-900 dark:text-white">{t('dashboard.taskLists')}</h2>
            </div>
            <button
              onClick={() => handleCreateList('task')}
              data-testid="create-task-list-btn"
              className="rounded-full border-2 border-[#18181B] bg-[#67E8F9] px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold hover:bg-[#A5F3FC] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center gap-1.5 sm:gap-2"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">{t('dashboard.newList')}</span>
              <span className="xs:hidden">{t('dashboard.new')}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {taskLists.map((list) => {
              // Find associated reward list for this task list
              const associatedRewardList = rewardLists.find(rl => rl.task_list_id === list.list_id);
              
              return (
                <div
                  key={list.list_id}
                  data-testid={`task-list-${list.list_id}`}
                  className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl sm:rounded-2xl hard-shadow p-4 sm:p-6 hover:translate-y-[-4px] hover:shadow-[6px_6px_0px_0px_rgba(24,24,27,1)] transition-all"
                >
                  <div 
                    className="cursor-pointer"
                    onClick={() => navigate(`/list/${list.list_id}`)}
                  >
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <h3 className="text-base sm:text-xl font-bold break-words pr-2 text-zinc-900 dark:text-white">{list.name}</h3>
                      {list.shared_with?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {list.shared_with.length}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mb-3">
                      <span className="px-2 py-0.5 sm:py-1 bg-zinc-100 dark:bg-zinc-700 rounded-md font-mono text-zinc-600 dark:text-zinc-300">
                        {list.owner_id === user?.user_id ? t('dashboard.owner') : t('dashboard.shared')}
                      </span>
                      <span className="px-2 py-0.5 sm:py-1 bg-[#FFF9F0] dark:bg-zinc-700 border border-[#18181B] dark:border-zinc-600 rounded-md font-mono text-zinc-900 dark:text-white">
                        {list.user_points ?? list.points ?? 0} pts
                      </span>
                    </div>
                  </div>
                  
                  {/* Rewards button */}
                  <div data-testid="task-list-rewards-info" className="pt-2 border-t border-zinc-100 dark:border-zinc-700">
                    {associatedRewardList ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/rewards/${associatedRewardList.list_id}`);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-[#ADE498] border-2 border-[#18181B] rounded-lg text-xs sm:text-sm font-bold hover:bg-[#bdeaa8] transition-colors"
                      >
                        <Gift className="w-4 h-4" />
                        {t('dashboard.viewRewards')}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateList('reward', list.list_id);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-[#FFC247] border-2 border-[#18181B] rounded-lg text-xs sm:text-sm font-bold hover:bg-[#FFD070] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
                      >
                        <Plus className="w-4 h-4" />
                        {t('dashboard.newList')} {t('rewards.rewards')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {taskLists.length === 0 && (
              <div className="col-span-full text-center py-8 sm:py-12 text-zinc-400 dark:text-zinc-500">
                <List className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-sm sm:text-base">{t('dashboard.noTaskLists')}</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer with legal links */}
      <footer className="max-w-7xl mx-auto mt-8 mb-20 pt-6 border-t border-zinc-200">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-xs text-zinc-400">
          <span>© 2026 Motivator</span>
          <span className="hidden sm:inline">·</span>
          <Link 
            to="/privacy-policy" 
            className="flex items-center gap-1.5 hover:text-zinc-600 transition-colors"
            data-testid="privacy-link"
          >
            <Shield className="w-3 h-3" />
            {t('legal.privacyPolicy')}
          </Link>
          <span className="hidden sm:inline">·</span>
          <Link 
            to="/delete-account" 
            className="flex items-center gap-1.5 hover:text-red-500 transition-colors"
            data-testid="delete-account-link"
          >
            <Trash2 className="w-3 h-3" />
            {t('legal.deleteAccount')}
          </Link>
          <span className="hidden sm:inline">·</span>
          <button
            onClick={() => {
              const feedbackBtn = document.querySelector('[data-testid="feedback-btn"]');
              if (feedbackBtn) feedbackBtn.click();
            }}
            className="flex items-center gap-1.5 hover:text-[#FF8BA7] transition-colors"
            data-testid="report-bug-link"
          >
            <Bug className="w-3 h-3" />
            {t('feedback.reportBug')}
          </button>
          {!isStandalone && (
            <>
              <span className="hidden sm:inline">·</span>
              <button
                onClick={handleInstallClick}
                className="flex items-center gap-1.5 hover:text-[#4CAF50] transition-colors"
                data-testid="install-app-link"
              >
                <Download className="w-3 h-3" />
                {t('install.downloadApp')}
              </button>
            </>
          )}
        </div>
      </footer>

      {/* Help button to restart tutorial */}
      <button
        onClick={resetTutorial}
        className="fixed bottom-4 right-4 w-12 h-12 rounded-full bg-[#FF8BA7] border-2 border-[#18181B] flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-40"
        title={t('tutorial.restartTutorial')}
        data-testid="help-btn"
      >
        <HelpCircle className="w-6 h-6 text-[#18181B]" />
      </button>

      {showCreateModal && (
        <CreateListModal
          type={modalType}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTaskListId(null);
          }}
          onCreated={handleListCreated}
          preselectedTaskListId={selectedTaskListId}
        />
      )}

      {/* Statistics Modal */}
      {showStatsModal && stats && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50" 
            onClick={() => setShowStatsModal(false)}
          />
          <div className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow z-50 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="stats-modal">
            {/* Header */}
            <div className="bg-[#FFC247] p-4 border-b-2 border-[#18181B] dark:border-zinc-600 sticky top-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-[#18181B]/70 tracking-wide">{t('dashboard.totalPoints')}</p>
                  <p className="text-3xl sm:text-4xl font-mono font-bold text-[#18181B]">{stats.total_points || 0}</p>
                </div>
                <button
                  onClick={() => setShowStatsModal(false)}
                  className="p-2 rounded-full hover:bg-[#18181B]/10 transition-colors"
                >
                  <X className="w-5 h-5 text-[#18181B]" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                <div className="bg-[#ADE498]/20 dark:bg-[#ADE498]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <CheckCircle className="w-3.5 h-3.5 text-[#4CAF50]" />
                    <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400">{t('dashboard.completed')}</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono font-bold text-zinc-900 dark:text-white">{stats.completed_tasks}</p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">{stats.completion_rate}% {t('dashboard.rate')}</p>
                </div>
                
                <div className="bg-[#FF8BA7]/20 dark:bg-[#FF8BA7]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Clock className="w-3.5 h-3.5 text-[#FF8BA7]" />
                    <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400">{t('dashboard.pending')}</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono font-bold text-zinc-900 dark:text-white">{stats.pending_tasks}</p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">{t('dashboard.ofTotal', { total: stats.total_tasks })}</p>
                </div>
                
                <div className="bg-[#FFC247]/20 dark:bg-[#FFC247]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Calendar className="w-3.5 h-3.5 text-[#FFC247]" />
                    <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400">{t('dashboard.thisWeek')}</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono font-bold text-[#4CAF50]">{stats.tasks_this_week}</p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">+{stats.points_this_week} pts</p>
                </div>
                
                <div className="bg-[#3B82F6]/20 dark:bg-[#3B82F6]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Flame className="w-3.5 h-3.5 text-[#F97316]" />
                    <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400">{t('dashboard.streak')}</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono font-bold text-[#3B82F6]">{stats.current_streak}</p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">{t('dashboard.consecutiveDays')}</p>
                </div>
              </div>

              {/* Weekly Progress Chart */}
              {stats.daily_stats && stats.daily_stats.length > 0 && (
                <div className="mb-4 p-3 bg-zinc-50 dark:bg-zinc-700 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-[#18181B] dark:text-white" />
                    <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">{t('dashboard.weeklyProgress')}</p>
                  </div>
                  
                  {/* Bar Chart */}
                  <div className="flex items-end justify-between gap-1 h-24 mb-2">
                    {stats.daily_stats.map((day, idx) => {
                      const maxTasks = Math.max(...stats.daily_stats.map(d => d.tasks), 1);
                      const heightPercent = day.tasks > 0 ? Math.max((day.tasks / maxTasks) * 100, 15) : 8;
                      const isToday = idx === stats.daily_stats.length - 1;
                      return (
                        <div key={day.date} className="flex-1 flex flex-col justify-end h-full">
                          <div 
                            className={`w-full rounded-t transition-all ${
                              day.tasks > 0 
                                ? isToday ? 'bg-[#FFC247]' : 'bg-[#ADE498]'
                                : 'bg-zinc-200 dark:bg-zinc-600'
                            } border-2 border-[#18181B] dark:border-zinc-500`}
                            style={{ height: `${heightPercent}%`, minHeight: day.tasks > 0 ? '20px' : '8px' }}
                            title={`${day.tasks} ${t('dashboard.tasks')}, ${day.points} pts`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Day Labels */}
                  <div className="flex justify-between">
                    {stats.daily_stats.map((day, idx) => (
                      <span 
                        key={day.date} 
                        className={`text-[9px] font-bold flex-1 text-center ${
                          idx === stats.daily_stats.length - 1 ? 'text-[#FFC247]' : 'text-zinc-500 dark:text-zinc-400'
                        }`}
                      >
                        {day.day}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Stats */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 bg-zinc-100 dark:bg-zinc-700 rounded-lg border border-zinc-200 dark:border-zinc-600">
                  <TrendingUp className="w-4 h-4 mx-auto mb-1 text-[#4CAF50]" />
                  <p className="text-lg font-mono font-bold text-zinc-900 dark:text-white">{stats.avg_tasks_per_day || 0}</p>
                  <p className="text-[9px] text-zinc-500 dark:text-zinc-400">{t('dashboard.avgPerDay')}</p>
                </div>
                <div className="text-center p-2 bg-zinc-100 dark:bg-zinc-700 rounded-lg border border-zinc-200 dark:border-zinc-600">
                  <Award className="w-4 h-4 mx-auto mb-1 text-[#FFC247]" />
                  <p className="text-lg font-mono font-bold text-zinc-900 dark:text-white">{stats.best_day_tasks || 0}</p>
                  <p className="text-[9px] text-zinc-500 dark:text-zinc-400">{t('dashboard.bestDay')}</p>
                </div>
                <div className="text-center p-2 bg-zinc-100 dark:bg-zinc-700 rounded-lg border border-zinc-200 dark:border-zinc-600">
                  <Gift className="w-4 h-4 mx-auto mb-1 text-[#FF8BA7]" />
                  <p className="text-lg font-mono font-bold text-zinc-900 dark:text-white">{stats.redeemed_rewards || 0}</p>
                  <p className="text-[9px] text-zinc-500 dark:text-zinc-400">{t('dashboard.rewardsRedeemed')}</p>
                </div>
              </div>

              {/* Points by list */}
              {stats.points_by_list && stats.points_by_list.length > 0 && (
                <div className="pt-3 border-t border-zinc-200 dark:border-zinc-600">
                  <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2">{t('dashboard.pointsByList')}</p>
                  <div className="space-y-1.5">
                    {stats.points_by_list.map((list) => (
                      <div 
                        key={list.list_id}
                        className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-700 rounded-lg px-3 py-1.5"
                      >
                        <span className="text-xs font-medium truncate mr-2 text-zinc-900 dark:text-white">{list.name}</span>
                        <span className="text-xs font-mono font-bold text-[#FFC247] bg-[#18181B] px-2 py-0.5 rounded">
                          {list.points} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Install App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-[#4CAF50]" />
                {t('install.title')}
              </h2>
              <button
                onClick={() => setShowInstallModal(false)}
                className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-900 dark:text-white" />
              </button>
            </div>
            
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
              {t('install.description')}
            </p>
            
            {isIOS ? (
              <div className="space-y-3">
                <p className="text-sm font-bold text-zinc-900 dark:text-white">{t('install.iosTitle')}</p>
                <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('install.iosStep1')}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('install.iosStep2')}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('install.iosStep3')}</p>
                </div>
              </div>
            ) : deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                className="w-full rounded-full border-2 border-[#18181B] bg-[#4CAF50] text-white font-bold py-3 hover:bg-[#43A047] transition-all flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                {t('install.installButton')}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-bold text-zinc-900 dark:text-white">{t('install.manualTitle')}</p>
                <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('install.manualStep1')}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('install.manualStep2')}</p>
                </div>
              </div>
            )}
            
            <button
              onClick={() => setShowInstallModal(false)}
              className="w-full mt-4 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-2.5 hover:bg-zinc-100 dark:hover:bg-zinc-600 transition-all"
            >
              {t('modals.close')}
            </button>
          </div>
        </div>
      )}

      {/* Tutorial Components */}
      {showCarousel && (
        <WelcomeCarousel onComplete={completeCarousel} />
      )}

      {showDemo && (
        <InteractiveDemo 
          onComplete={() => {
            completeDemo();
            // Refresh data after demo creates lists
            fetchData();
          }} 
        />
      )}
      
      {showTour && !loading && (
        <GuidedTour 
          steps={tourSteps} 
          onComplete={completeTour}
          isActive={showTour}
        />
      )}
    </div>
  );
}
